import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/usePortfolioDocumentsQuery";
import {
	ClientPropertyCategoryEnum,
	type ClientPropertyCottageDetailResponseDTO,
	ClientPropertyOwnershipEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyDataCard } from "web_myplann/Portfolio/Components/PortfolioTools/PropertyDataCard";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import {
	getBuildingMaterial,
	getDisposition,
} from "web_myplann/Portfolio/Helpers/utils";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	dateFormatter,
	unitFormatter,
} from "web_myplann/Shared/Utils/formatters";

const PageResources = Resources.PortfolioDetail;

const propTypes = [
	{ key: "terrace", label: PageResources.Property.Terrace },
	{
		key: "loggia",
		label: PageResources.Property.Loggia,
	},
	{
		key: "balcony",
		label: PageResources.Property.Balcony,
	},
	{
		key: "garage",
		label: PageResources.Property.Garage,
	},
	{
		key: "cellar",
		label: PageResources.Property.Cellar,
	},
	{
		key: "elevator",
		label: PageResources.Property.Elevator,
	},
	{
		key: "barrierFree",
		label: PageResources.Property.BarrierFree,
	},
	{
		key: "swimmingPool",
		label: PageResources.Property.SwimmingPool,
	},
	{
		key: "parkingSpace",
		label: PageResources.Property.ParkingSpace,
	},
	{
		key: "furnished",
		label: PageResources.Property.Furnished,
	},
];

type Props = {
	data: ClientPropertyCottageDetailResponseDTO;
	currencySign: string;
	documentsId: number;
};

export const Cottage: FC<Props> = ({ data, currencySign, documentsId }) => {
	const { t } = useResource();

	const { data: documents } = usePortfolioDocumentsQuery(documentsId);
	const { properties } = useGetPropertyOptionalParams(propTypes, data, t);

	const ownership =
		data?.ownership && data.ownership !== ClientPropertyOwnershipEnum.None
			? t(PageResources.Property.OwnershipValue[data.ownership])
			: "";

	const buildingMaterial = getBuildingMaterial(data, t);
	const disposition = getDisposition(data, t);

	const propertyCategory =
		data?.propertyCategory &&
		data.propertyCategory !== ClientPropertyCategoryEnum.None
			? t(PageResources.Property.PropertyCategoryValue[data.propertyCategory])
			: "";

	const bottomComponent =
		data.purchaseDate || data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.PurchaseDate)}
					value={dateFormatter(data.purchaseDate)}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description ?? ""}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
			>
				<Stack>
					<DataRow
						label={t(PageResources.Property.TypeOfConstruction)}
						value={propertyCategory}
					/>
					<DataRow
						label={t(PageResources.Property.StreetAndNumber)}
						value={data.location}
					/>
					<DataRow
						label={t(PageResources.Property.Municipality)}
						value={data.locationMunicipality}
					/>
					<DataRow
						label={t(PageResources.Property.ZipCode)}
						value={data.locationZipCode}
					/>
				</Stack>
			</DataCard>

			<PropertyDataCard
				reproductionValue={data.reproductionValue}
				interiorPrice={data.interiorValue}
				marketValue={data.marketValue}
				priceHistory={data.estimatedPriceHistoryList}
				currencySign={currencySign}
				type={ClientPropertyTypeEnum.Cottage}
			/>

			<DataCard preset={DataCardPreset.PropertyParameters}>
				<Stack>
					<DataRow
						label={t(PageResources.Property.Disposition)}
						value={disposition}
					/>
					<DataRow
						label={t(PageResources.Property.NumberOfAboveGroundFloors)}
						value={data?.numberOfFloors}
					/>
					<DataRow
						label={t(PageResources.Property.BuildingMaterial)}
						value={buildingMaterial}
					/>
					<DataRow
						label={t(PageResources.Property.FloorArea)}
						value={unitFormatter(data.floorArea)}
					/>
					<DataRow
						label={t(PageResources.Property.UsableArea)}
						value={unitFormatter(data.usableArea)}
					/>
					<DataRow
						label={t(PageResources.Property.LandArea)}
						value={unitFormatter(data.landSize)}
					/>
					<DataRow
						label={t(PageResources.Property.SecondaryBuildingArea)}
						value={unitFormatter(data.secondaryBuildingArea)}
					/>
					<DataRow
						label={t(PageResources.Property.Ownership)}
						value={ownership}
					/>
					<DataNote
						label={t(PageResources.Property.Properties)}
						text={properties}
					/>
				</Stack>
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
